import MuiAlert, { type AlertColor } from '@mui/material/Alert';
import type { SxProps, Theme } from '@mui/material/styles';
import { styled } from '@mui/material/styles';

const getInlineAlertAccentColor = (theme: Theme, severity: AlertColor): string => {
  switch (severity) {
    case 'error':
      return theme.palette.error.main;
    case 'info':
      return theme.palette.info.main;
    case 'success':
      return theme.palette.success.main;
    case 'warning':
      return theme.palette.warning.main;
    default:
      return theme.palette.info.main;
  }
};

const createInlineAlertSx = (severity: AlertColor): SxProps<Theme> => ({
  alignItems: 'center',
  backgroundColor: 'background.paper',
  borderColor: (theme: Theme) => getInlineAlertAccentColor(theme, severity),
  borderRadius: '6px',
  borderStyle: 'solid',
  borderWidth: '2px',
  color: (theme: Theme) => getInlineAlertAccentColor(theme, severity),
  gap: '16px',
  padding: '16px',
  '& .MuiAlert-icon': {
    alignItems: 'center',
    color: (theme: Theme) => getInlineAlertAccentColor(theme, severity),
    display: 'flex',
    height: 22,
    justifyContent: 'center',
    marginRight: 0,
    padding: 0,
    width: 22,
  },
  '& .MuiAlert-icon .MuiSvgIcon-root': {
    fontSize: 22,
  },
  '& .MuiAlert-message': {
    color: (theme: Theme) => getInlineAlertAccentColor(theme, severity),
    fontSize: 15,
    fontStyle: 'normal',
    fontWeight: 700,
    letterSpacing: 0,
    lineHeight: 1.3334,
    padding: 0,
  },
  '& .MuiAlert-action': {
    color: (theme: Theme) => getInlineAlertAccentColor(theme, severity),
    marginLeft: 0,
    marginRight: 0,
    padding: 0,
  },
  '& .MuiAlert-action .MuiIconButton-root': {
    alignItems: 'center',
    color: (theme: Theme) => getInlineAlertAccentColor(theme, severity),
    display: 'flex',
    height: 22,
    justifyContent: 'center',
    margin: 0,
    padding: 0,
    width: 22,
  },
  '& .MuiAlert-action .MuiSvgIcon-root': {
    fontSize: 22,
  },
});

const alertInlineSxBySeverity: Record<AlertColor, SxProps<Theme>> = {
  error: createInlineAlertSx('error'),
  info: createInlineAlertSx('info'),
  success: createInlineAlertSx('success'),
  warning: createInlineAlertSx('warning'),
};

export const getAlertInlineSx = (severity: AlertColor): SxProps<Theme> => (
  alertInlineSxBySeverity[severity]
);

export const StyledAlert = styled(MuiAlert)(({ theme }) => ({
  borderRadius: 14,
  alignItems: 'center',
  '& .MuiAlert-icon': {
    alignItems: 'center',
    display: 'flex',
  },
  '& .MuiAlert-message': {
    flex: '1 1 auto',
    fontSize: 16,
    fontWeight: 700,
    lineHeight: 1.25,
    minWidth: 0,
    overflow: 'visible',
  },
  '&:focus-visible': {
    outline: `3px solid ${theme.palette.action.focus}`,
    outlineOffset: 2,
  },
}));
